import { Help, HelpService } from '../../help';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Component, OnInit, Input, EventEmitter, Output, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AlertService } from '@app/_services';
import * as moment from 'moment';
import { PrimeNGConfig } from 'primeng/api';
import { MsgTemplateService } from '@app/_components/msg-template/msg-template.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
    selector: 'app-help',
    templateUrl: './help.component.html',
    styleUrls: ['./help.component.css'],
    providers: [MessageService, ConfirmationService],
    standalone: false
})
export class HelpComponent implements OnInit {

	constructor(
		private primengConfig: PrimeNGConfig,
		private helpService: HelpService,
		private alertService: AlertService,
		private messageService: MessageService,
		private confirmationService: ConfirmationService,
		private formBuilder: UntypedFormBuilder,
		private msgTemplateService: MsgTemplateService,
		private sanitizer: DomSanitizer,
		@Inject(MAT_DIALOG_DATA) data: any) {
		if ( data){
			this.formTitle = data.title,
				this.help = data.help;
			this.formReadOnly = data.formReadOnly;
		}
	}

	@Input() help: Help;
	@Output() doAdd = new EventEmitter<any>();
	@Output() doUpdate = new EventEmitter<any>();
	@Output() doDelete = new EventEmitter<any>();
	@Input() formReadOnly = false;
	// data
	helpId: number;

	// forms
	form1: UntypedFormGroup;
	formLoading = false;
	formAlert: String;
	formTitle: String;

	statuses: any;

	// template variables
	templateVarGroups: { group: string; vars: string[] }[] = [];
	templateVarsLoading = false;
	expandedGroups: { [group: string]: boolean } = {};
	templateVarSearch: string = '';

	get filteredTemplateVarGroups(): { group: string; vars: string[] }[] {
		const q = (this.templateVarSearch || '').trim().toLowerCase();
		if (!q) return this.templateVarGroups;
		return this.templateVarGroups
			.map(grp => ({
				group: grp.group,
				vars: grp.vars.filter(v => v.toLowerCase().includes(q))
			}))
			.filter(grp => grp.vars.length > 0);
	}

	onTemplateVarSearch() {
		const q = (this.templateVarSearch || '').trim().toLowerCase();
		if (q) {
			// auto-expand all groups that have matches
			this.filteredTemplateVarGroups.forEach(grp => {
				this.expandedGroups[grp.group] = true;
			});
		}
	}

	highlightMatch(text: string): SafeHtml {
		const q = (this.templateVarSearch || '').trim();
		if (!q) return text;
		const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
		const highlighted = text.replace(new RegExp(`(${escaped})`, 'gi'), '<mark style="background:#fff3cd;padding:0 2px;border-radius:2px;">$1</mark>');
		return this.sanitizer.bypassSecurityTrustHtml(highlighted);
	}

	validationMessages = {
		'helpId': [],
		'name': [
			{ type: 'required', message: 'Required' },
			{ type: 'minlength', message: '5 characters minimum' }],
		'description': [
			{ type: 'required', message: 'Required' }
		],
		'status': [],
		'helpDate': [],
		'isHelp': [],
	};

	ngOnInit() {
		this.primengConfig.ripple = true;
		this.formBuild();

		// if we were passed the whole help object
		if (this.help) {
			this.helpId = this.help.helpId;
			this.form1.patchValue(this.help);
		}

		// load template variable groups from backend
		this.templateVarsLoading = true;
		this.msgTemplateService.getTemplateVars().subscribe(
			(res: any) => {
				this.templateVarGroups = res?.data?.templateVarGroups || [];
				// expand first group by default
				if (this.templateVarGroups.length > 0) {
					this.expandedGroups[this.templateVarGroups[0].group] = true;
				}
			},
			(err: any) => {
				console.log('getTemplateVars FAIL: ' + JSON.stringify(err));
			},
			() => { this.templateVarsLoading = false; }
		);
	}

	toggleGroup(group: string) {
		this.expandedGroups[group] = !this.expandedGroups[group];
	}

	copyToClipboard(text: string) {
		navigator.clipboard.writeText(text).then(() => {
			this.messageService.add({ severity: 'success', summary: 'Copied', detail: text, life: 1500 });
		});
	}

	onFormDelete() {
		console.log('onRowDelete: ' + JSON.stringify(event));
		this.confirmationService.confirm({
			message: 'Are you sure you want to delete Id# ' + this.helpId + '?',
			header: 'Confirm',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				this.formDelete()
			},
			reject: () => {
				this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'Delete canceled.', life: 3000 });
			}
		});
	}

	formAdd() {
		try {
			// stop here if form is invalid
			this.form1.markAllAsTouched();
			if (this.form1.invalid) {
				return;
			}
			console.log('property-add post: ' + JSON.stringify(this.form1.value));
			this.formLoading = true;
			this.helpService.add(this.form1.value)
				.subscribe(
					(res: any) => {
						console.log('property-add SUCCESS: ' + JSON.stringify(res));
						this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
						this.help = res.data.help;
						this.doAdd.emit(this.help);
					},
					(err: any) => {
						console.log('help-add FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });

		} catch (error) {
			this.alertService.error(error);
		}
	}

	formUpdate() {
		console.log('help: ' + JSON.stringify(this.form1.value));
		try {
			// stop here if form is invalid
			this.form1.markAllAsTouched();
			if (this.form1.invalid) {
				return;
			}
			this.formLoading = true;

			this.helpService.update(this.form1.value)
				.subscribe(
					(res: any) => {
						console.log('property-update SUCCESS: ' + JSON.stringify(res));
						this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
						this.help = res.data.help;
						this.doUpdate.emit(this.help);
					},
					(err: any) => {
						console.log('property-update FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });
		} catch (error) {
			console.log('Form Invalid: ' + JSON.stringify(error));
			this.alertService.error(error);
		}
	}

	formDelete() {
		try {
			this.helpService.delete(this.help.helpId)
				.subscribe(
					(res: any) => {
						console.log('help-delete SUCCESS: ' + JSON.stringify(res));
						this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
						this.doDelete.emit(this.help);
					},
					(err: any) => {
						console.log('help-add FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });
		} catch (error) {
			this.alertService.error(error);
		}
	}

	formBuild() {
		this.form1 = this.formBuilder.group({
			helpId: [this.helpId],
			name: ['', Validators.compose(
				[
					Validators.minLength(5),
					Validators.required
				])],
			miniDoc: ['', Validators.compose(
				[
					Validators.minLength(5),
					Validators.required
				])]
		});
	}
}
